import confetti from 'canvas-confetti';

const BURST_COLORS = ['#f28c00', '#ff3b00', '#ffd700', '#ff7a00', '#ffae42', '#ffcc33'];
const CONFETTI_COLORS = BURST_COLORS.slice(0, 5);
const PARTICLE_COUNT = 64;

const DEFAULT_SCAN_MS = {
    ring: 600,
    particles: 750,
    status: 850,
    end: 2600,
    cleanup: 3200,
} as const;

export type ScanTimings = typeof DEFAULT_SCAN_MS;

interface Point {
    x: number;
    y: number;
}

interface ScanSequenceCallbacks {
    onStatus: () => void;
    onEnd: () => void;
}

function centerOf(el: HTMLElement): Point {
    const {left, top, width, height} = el.getBoundingClientRect();
    return {x: left + width / 2, y: top + height / 2};
}

function rand(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function delay(ms: number, fn: () => void): number {
    return window.setTimeout(fn, ms);
}

function appendAt(
    c: Point,
    className: string,
    styles: Partial<CSSStyleDeclaration> = {},
    cssVars: Record<string, string> = {},
): void {
    const node = document.createElement('div');
    node.className = className;
    node.style.left = `${c.x}px`;
    node.style.top = `${c.y}px`;
    Object.assign(node.style, styles);
    Object.entries(cssVars).forEach(([key, value]) => node.style.setProperty(key, value));
    document.body.appendChild(node);
}

function fireConfetti(c: Point): void {
    const origin = {x: c.x / window.innerWidth, y: c.y / window.innerHeight};
    confetti({particleCount: 130, spread: 95, startVelocity: 48, origin, colors: CONFETTI_COLORS});
    confetti({particleCount: 70, spread: 130, startVelocity: 32, origin, colors: CONFETTI_COLORS, scalar: 0.9});
}

function spawnParticle(c: Point): void {
    const angle = Math.random() * Math.PI * 2;
    const speed = rand(170, 530);
    const size = rand(4, 13);
    const color = BURST_COLORS[Math.floor(Math.random() * BURST_COLORS.length)];
    const strip = Math.random() < 0.4;

    appendAt(c, 'qr-particle', {
        width: `${strip ? size * 0.3 : size}px`,
        height: `${strip ? size * 2.2 : size}px`,
        background: color,
        borderRadius: strip ? '2px' : '50%',
        boxShadow: `0 0 8px 2px ${color}99`,
        animationDuration: `${rand(2, 2.7)}s`,
        marginLeft: `${-size / 2}px`,
        marginTop: `${-size / 2}px`,
    }, {
        '--dx': `${Math.cos(angle) * speed}px`,
        '--dy': `${Math.sin(angle) * speed - 90}px`,
        '--r0': `${Math.random() * 360}deg`,
        '--r1': `${rand(-450, 450)}deg`,
    });
}

function spawnParticles(c: Point): void {
    Array.from({length: PARTICLE_COUNT}, () => spawnParticle(c));
}

function runRingBurst(c: Point): void {
    appendAt(c, 'qr-ring');
    fireConfetti(c);
}

export function clearQrScanBurst(): void {
    document.querySelectorAll('.qr-ring, .qr-particle').forEach(el => el.remove());
}

export function scheduleQrScanSequence(
    el: HTMLElement,
    callbacks: ScanSequenceCallbacks,
    timings: ScanTimings = DEFAULT_SCAN_MS,
): () => void {
    const c = centerOf(el);
    const {onStatus, onEnd} = callbacks;
    const timers = [
        delay(timings.ring, () => runRingBurst(c)),
        delay(timings.particles, () => spawnParticles(c)),
        delay(timings.status, onStatus),
        delay(timings.end, onEnd),
        delay(timings.cleanup, clearQrScanBurst),
    ];

    return () => {
        timers.forEach(window.clearTimeout);
        clearQrScanBurst();
    };
}
